// Mini-galería editorial de la ficha (DESKTOP) — sección "Galería" bajo el mapa.
//
// Usa SOLO las imágenes adicionales del registro (space_images / event_images); la
// portada/hero NUNCA se repite aquí (el caller ya la excluye). La PRIMERA imagen es la
// principal y lleva mayor peso visual; las demás la acompañan en una subcomposición
// ASIMÉTRICA — nunca un grid rígido tipo "portarretrato".
//
// Máximo 4 tiles visibles. Si hay más imágenes, la última lleva overlay "+N" (N =
// imágenes adicionales no mostradas). Footprint fijo (aspect-[2/1]) → la cantidad de
// imágenes no altera el alto de la sección. Mismo lenguaje visual de la ficha (radio
// 18px, gap 8px, borde white/20). Render-only: sin lightbox / navegación todavía.

import Image from 'next/image';
import React from 'react';

type GalleryImage = { url: string; alt: string; unoptimized?: boolean };

const MAX_TILES = 4;
const tileCls =
  'relative overflow-hidden rounded-[18px] border border-white/20 shadow-sm';

// Subcomposición de las acompañantes (a la derecha de la principal), por nº de ellas.
// Con 3, la primera acompañante va ancha arriba → asimetría editorial, no un 2×2 plano.
const COMPANION_GRID: Record<number, string> = {
  1: 'grid-rows-1',
  2: 'grid-rows-2',
  3: 'grid-cols-2 grid-rows-2',
};
const COMPANION_SPANS: Record<number, string[]> = {
  1: [''],
  2: ['', ''],
  3: ['col-span-2', '', ''],
};

export default function SpaceGalleryGrid({
  images = [],
}: {
  images?: GalleryImage[];
}) {
  if (!images.length) return null;

  const tiles = images.slice(0, MAX_TILES);
  const extra = images.length - tiles.length; // imágenes adicionales no mostradas

  const renderImg = (img: GalleryImage) => (
    <Image
      alt={img.alt}
      src={img.url}
      fill
      sizes="(max-width:1024px) 100vw, 520px"
      quality={90}
      className="object-cover object-center"
      unoptimized={img.unoptimized}
    />
  );

  // 1 imagen → una sola tile a todo el ancho/alto de la galería.
  if (tiles.length === 1) {
    return (
      <div className="w-full aspect-[2/1]">
        <div className={`${tileCls} w-full h-full`}>{renderImg(tiles[0])}</div>
      </div>
    );
  }

  // 2+ → principal (izquierda, mayor peso) + acompañantes a la derecha (asimétrico).
  const [principal, ...companions] = tiles;
  const cgrid = COMPANION_GRID[companions.length] || 'grid-rows-1';
  const cspans = COMPANION_SPANS[companions.length] || [];
  return (
    <div className="w-full aspect-[2/1]">
      <div className="grid h-full w-full gap-2 grid-cols-[1.4fr_1fr]">
        <div className={tileCls}>{renderImg(principal)}</div>
        <div className={`grid h-full gap-2 ${cgrid}`}>
          {companions.map((img, i) => {
            const isLast = i === companions.length - 1;
            return (
              <div key={i} className={`${tileCls} ${cspans[i] || ''}`}>
                {renderImg(img)}
                {isLast && extra > 0 && (
                  <div className="absolute inset-0 bg-black/55 backdrop-blur-[1px] flex items-center justify-center">
                    <span className="text-white text-xl font-semibold tracking-tight">
                      +{extra}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
